import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { useFormik } from 'formik';
import { getModelos } from '../../api/ingreso-manual-client';
import { MODO_DEFENSA } from '../../common/config-ingreso-manual';
import {
    ciudadDeComuna, erroresAMapa, esquemaPara, EXTENSIONES_CARTA, hoyISO, nombreCiudadDeComuna, nombreDe, patenteValida, porcentajeConvenio
} from '../../common/ingreso-manual';
import { Archivo, Combo, Monto, Rut, Texto, TextoLectura } from './campos';

// Icono de documento PDF (SVG propio: el módulo no trae una librería de iconos).
const IconoPdf = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" role="img" aria-hidden="true" style={{ display: 'block' }}>
        <path d="M6 2h8l6 6v14H6z" fill="#fff" stroke="#c0392b" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M14 2v6h6" fill="none" stroke="#c0392b" strokeWidth="1.5" strokeLinejoin="round" />
        <rect x="2.5" y="11.5" width="15" height="7.5" rx="1" fill="#c0392b" />
        <text x="10" y="17.2" textAnchor="middle" fontSize="5.6" fontWeight="700" fill="#fff" fontFamily="Arial, sans-serif">PDF</text>
    </svg>
);

// Los datos obligatorios van siempre visibles; todo lo demás es opcional y va en pestañas.
const CAMPOS_PLANOS = ['siniestro', 'patente', 'fechaSiniestro', 'fechaDenuncio', 'tipoSiniestro', 'idResponsable'];

// En modo Defensa estos campos van en la primera pestaña («Datos de la carta»): no cuentan para las pestañas opcionales.
const CAMPOS_DEFENSA = [
    'idCiaImplicado', 'siniestroIM', 'fechaSiniestroIM', 'terPatente', 'terMarca', 'terDescripcion', 'terRut', 'terNombre', // bloque «Tercero implicado»
    'costo', 'montoPagar', 'fechaRecepcionCarta', 'notasPreAnalisis', 'cartaArchivo'                                       // bloque «Carta de cobro»
];

const PESTANA_PRINCIPAL = 'principal';

// Campos de cada pestaña (para marcar la que tiene errores y llevar al usuario a ella).
const PESTANAS = [
    { clave: 'siniestro', titulo: 'Datos del siniestro', campos: ['hora', 'comunaSiniestro', 'ciudadSiniestro', 'lugarSiniestro', 'causal', 'poliza', 'itemPoliza', 'vigenciaDesde', 'vigenciaHasta', 'relato'] },
    { clave: 'vehiculo', titulo: 'Vehículo asegurado', campos: ['tipoVehiculo', 'marcaVehiculo', 'codModelo', 'anioVehiculo', 'colorVehiculo', 'usoVehiculo'] },
    { clave: 'asegurado', titulo: 'Asegurado', campos: ['aseRut', 'aseNombre', 'aseFono', 'aseDireccion', 'aseComuna', 'aseCiudad'] },
    { clave: 'tercero', titulo: 'Tercero implicado', campos: ['terRut', 'terNombre', 'terPatente', 'terMarca', 'idCiaImplicado', 'siniestroIM', 'fechaSiniestroIM', 'terDescripcion'] },
    { clave: 'conductor', titulo: 'Conductor', campos: ['conRut', 'conNombre', 'conFono'] },
    { clave: 'montos', titulo: 'Montos (CLP)', campos: ['costo', 'provision'] },
    { clave: 'policial', titulo: 'Antecedente policial', campos: ['comisaria', 'folioPolicial', 'fechaConstancia'] }
];

// modoEdicion: { idCaso } cuando se está actualizando un caso existente; onVerificarExistente({ siniestro, patente }) avisa al padre
// cuando el usuario sale de esos campos (para el modal «Siniestro ya existe»); onCancelarEdicion vuelve a un ingreso nuevo.
// modo: 'RECUPERO' (abogado responsable elegido, recobro = costo) o 'DEFENSA' (cartas inter cía: responsable = quien ingresa, y datos de la carta).
const Formulario = ({ catalogos, valores, enviando, errorGeneral, onRevisar, modoEdicion, onVerificarExistente, onCancelarEdicion, onVerCarta, modo }) => {

    const defensa = modo === MODO_DEFENSA;
    // En cartas inter cía la primera pestaña reúne todo lo obligatorio (siniestro, tercero y carta de cobro).
    const [pestana, setPestana] = useState(defensa ? PESTANA_PRINCIPAL : PESTANAS[0].clave);

    // Los modelos dependen de la marca: se cargan al elegirla.
    const [modelos, setModelos] = useState([]);
    const [cargandoModelos, setCargandoModelos] = useState(false);
    const marcaSolicitada = useRef('');

    // Si hay errores en los datos del caso (siempre visibles) no se cambia de pestaña. Si no, en cartas inter cía se abre la pestaña
    // «Datos de la carta» cuando tiene errores; en los demás casos, la primera pestaña con error.
    const camposDePestana = (p) => (defensa ? p.campos.filter((c) => !CAMPOS_DEFENSA.includes(c)) : p.campos);
    const irAPestanaConError = (errores) => {
        if (CAMPOS_PLANOS.some((c) => errores[c])) return;
        if (defensa && CAMPOS_DEFENSA.some((c) => errores[c])) {
            setPestana(PESTANA_PRINCIPAL);
            return;
        }
        const conError = PESTANAS.find((p) => camposDePestana(p).some((c) => errores[c]));
        if (conError) setPestana(conError.clave);
    };

    const formik = useFormik({
        initialValues: valores, // Valores iniciales, con el abogado preseleccionado.
        validationSchema: esquemaPara(modo), // Esquema de validación de Yup (depende del flujo).
        onSubmit: async (values, helpers) => {
            // onRevisar devuelve los errores que detectó el servidor (o null si todo está bien).
            const erroresServidor = await onRevisar(values, helpers);
            if (erroresServidor && erroresServidor.length > 0) irAPestanaConError(erroresAMapa(erroresServidor));
        }
    });

    // Antes de enviar se validan los campos del formulario para abrir la pestaña con errores, si corresponde.
    const handleEnvio = async (event) => {
        event.preventDefault();
        const errores = await formik.validateForm();
        irAPestanaConError(errores);
        formik.submitForm();
    };

    const hoy = hoyISO();

    // La ciudad no se elige: se completa según la comuna seleccionada.
    const completaCiudad = (campoCiudad) => (idComuna) =>
        formik.setFieldValue(campoCiudad, ciudadDeComuna(catalogos.comunas, catalogos.ciudades, idComuna));

    // Al cambiar la marca se limpia el modelo y se cargan los modelos de la marca elegida. Si el usuario cambia de marca
    // mientras llega la respuesta, se ignora la de la marca anterior.
    const cargaModelos = async (codMarca) => {
        marcaSolicitada.current = codMarca;
        formik.setFieldValue('codModelo', '');
        formik.setFieldValue('modelo', '');
        setModelos([]);
        if (!codMarca) {
            setCargandoModelos(false);
            return;
        }
        setCargandoModelos(true);
        try {
            const lista = await getModelos(codMarca);
            if (marcaSolicitada.current === codMarca) setModelos(lista);
        } catch (error) {
            console.error(error);
        } finally {
            if (marcaSolicitada.current === codMarca) setCargandoModelos(false);
        }
    };

    // En modo edición el formulario llega con marca y modelo ya elegidos: se cargan los modelos de esa marca SIN limpiar el modelo.
    useEffect(() => {
        if (!valores.marcaVehiculo) return undefined;
        let vigente = true;
        marcaSolicitada.current = valores.marcaVehiculo;
        setCargandoModelos(true);
        getModelos(valores.marcaVehiculo)
            .then((lista) => {
                if (!vigente) return;
                setModelos(lista);
                formik.setFieldValue('modelo', nombreDe(lista, valores.codModelo));
            })
            .catch((error) => console.error(error))
            .finally(() => { if (vigente) setCargandoModelos(false); });
        return () => { vigente = false; };
        // Solo al montar: cada caso precargado remonta el formulario con otra clave.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Al salir de siniestro o patente, si ambos están y son válidos, se pregunta si ya existe un caso (no aplica en edición).
    const verificaExistente = () => {
        if (modoEdicion || !onVerificarExistente) return;
        const { siniestro, patente } = formik.values;
        if (String(siniestro || '').trim() && String(patente || '').trim() && patenteValida(patente)) {
            onVerificarExistente({ siniestro: String(siniestro).trim(), patente: String(patente).trim() });
        }
    };

    // «Fecha siniestro en la CIA» sigue a «Fecha siniestro» mientras no se haya cambiado a mano (está vacía o igual a la anterior), porque
    // el servidor guarda la fecha del siniestro cuando no se informa otra. Así lo que se ve es lo que se guarda. Solo en Recupero: en
    // cartas inter cía es la fecha del siniestro del tercero, que es otro dato y no se completa sola.
    const sincronizaFechaCia = (nuevaFechaSiniestro) => {
        if (defensa) return;
        const { fechaSiniestro: anterior, fechaSiniestroIM } = formik.values;
        if (!fechaSiniestroIM || fechaSiniestroIM === anterior) {
            formik.setFieldValue('fechaSiniestroIM', nuevaFechaSiniestro);
        }
    };

    const ayudaModelo = !formik.values.marcaVehiculo
        ? 'Seleccione primero la marca.'
        : cargandoModelos
            ? 'Cargando modelos...'
            : modelos.length === 0 ? 'La marca no tiene modelos registrados.' : undefined;

    const nombreCiudad = (idComuna) => nombreCiudadDeComuna(catalogos.comunas, catalogos.ciudades, idComuna);

    // Título de la pestaña, con una marca roja si tiene campos con errores (después de intentar revisar).
    const titulo = (p) => {
        const conError = formik.submitCount > 0 && camposDePestana(p).some((c) => formik.errors[c]);
        return (
            <span>
                {p.titulo}
                {conError ? <span className="text-danger ms-1" title="Hay campos por corregir">●</span> : null}
            </span>
        );
    };
    const tab = (clave) => PESTANAS.find((p) => p.clave === clave);

    // Título de la pestaña principal (cartas inter cía), con marca roja si algún obligatorio tiene error.
    const tituloPrincipal = (
        <span>
            Datos de la carta
            {formik.submitCount > 0 && CAMPOS_DEFENSA.some((c) => formik.errors[c])
                ? <span className="text-danger ms-1" title="Hay campos por corregir">●</span> : null}
        </span>
    );

    // Campos del siniestro: en Recupero van en su propia tarjeta; en cartas inter cía, dentro de la pestaña principal.
    const camposSiniestro = (
        <Row>
            <Texto formik={formik} name="siniestro" label="N° siniestro" requerido onSalida={verificaExistente} />
            <Texto formik={formik} name="patente" label="Patente asegurado" requerido onSalida={verificaExistente} />
            <Texto formik={formik} name="fechaSiniestro" label="Fecha siniestro" requerido type="date" max={hoy}
                onCambio={sincronizaFechaCia} />
            <Texto formik={formik} name="fechaDenuncio" label="Fecha denuncio" requerido type="date" max={hoy} />
            <Combo formik={formik} name="tipoSiniestro" label="Tipo de siniestro" requerido md={6} opciones={catalogos.tiposSiniestro} />
            {defensa ? null : (
                <Combo formik={formik} name="idResponsable" label="Abogado responsable" requerido md={6}
                    opciones={catalogos.responsables} deshabilitado={!!modoEdicion}
                    ayuda={modoEdicion ? 'No se puede cambiar al actualizar un caso.' : undefined} />
            )}
        </Row>
    );

    // Título de bloque dentro de una pestaña: banda con borde de color para que se distinga de las etiquetas de los campos.
    const subtitulo = (texto) => (
        <h6 className="mt-3 mb-3 py-2 px-3 fw-bold"
            style={{ color: '#4F517A', backgroundColor: '#ECECF4', borderLeft: '5px solid #4F517A', borderRadius: 4, fontSize: '1.05rem' }}>
            {texto}
        </h6>
    );

    return (
        <form noValidate onSubmit={handleEnvio}>

            {modoEdicion ? (
                <Alert variant="info">
                    <b>Modo edición:</b> está actualizando el caso N° {modoEdicion.idCaso}. Los cambios se guardan sobre ese caso; el {defensa ? 'responsable' : 'abogado responsable'} y la bandeja no cambian.
                </Alert>
            ) : null}

            {errorGeneral ? <Alert variant="danger">{errorGeneral}</Alert> : null}

            <Card className="mb-3">
                <Card.Header as="h6" className="text-white" style={{ backgroundColor: '#4F517A' }}>Datos del caso</Card.Header>
                <Card.Body>{camposSiniestro}</Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Header as="h6" className="text-white" style={{ backgroundColor: '#4F517A' }}>
                    {defensa ? 'Carta de cobro e información adicional' : 'Información adicional'}
                    {defensa ? null : <small className="ms-2 fw-normal">(opcional)</small>}
                </Card.Header>
                <Card.Body>
                    <Tabs id="ingreso-manual-pestanas" activeKey={pestana} onSelect={(k) => setPestana(k)} className="mb-3">

                        {defensa ? (
                            <Tab eventKey={PESTANA_PRINCIPAL} title={tituloPrincipal}>
                                {subtitulo('Tercero implicado y su compañía')}
                                <Row>
                                    <Combo formik={formik} name="idCiaImplicado" label="Compañía del tercero" requerido md={6} opciones={catalogos.companias} />
                                    <Texto formik={formik} name="siniestroIM" label="N° siniestro en la compañía" requerido md={3} />
                                    <Texto formik={formik} name="fechaSiniestroIM" label="Fecha siniestro en la CIA" type="date" max={hoy} />
                                    <Texto formik={formik} name="terPatente" label="Patente tercero" requerido />
                                    <Combo formik={formik} name="terMarca" label="Marca tercero" opciones={catalogos.marcas} />
                                    <Texto formik={formik} name="terDescripcion" label="Descripción del vehículo" md={6} />
                                    <Rut formik={formik} name="terRut" label="Rut tercero" />
                                    <Texto formik={formik} name="terNombre" label="Nombre tercero" md={9} />
                                </Row>

                                {subtitulo('Carta de cobro')}
                                <Row>
                                    <Monto formik={formik} name="costo" label="Valor neto solicitado (Monto neto)" requerido placeholder="Ej. 4.600.000" />
                                    <Monto formik={formik} name="montoPagar" label="Monto a pagar (Monto recobro)" requerido placeholder="Ej. 3.450.000" />
                                    <TextoLectura label="% convenio" valor={porcentajeConvenio(formik.values.costo, formik.values.montoPagar)}
                                        ayuda="Monto a pagar × 100 ÷ valor neto." />
                                    <Texto formik={formik} name="fechaRecepcionCarta" label="Fecha recepción carta" requerido type="date" max={hoy} />
                                    <Texto formik={formik} name="notasPreAnalisis" label="Notas pre-análisis" md={12} rows={2} />
                                    <Archivo formik={formik} name="cartaArchivo" label="Carta de cobro (opcional)" accept=".doc,.docx,.pdf"
                                        ayuda={(
                                            <React.Fragment>
                                                {modoEdicion && modoEdicion.tieneCarta ? (
                                                    <span className="d-block text-success">
                                                        ✔ El caso ya tiene una carta adjunta. Seleccione un archivo solo si desea reemplazarla.
                                                        {onVerCarta ? (
                                                            <Button variant="link" className="p-0 ms-2 align-middle" type="button" onClick={onVerCarta}
                                                                title="Ver la carta adjunta" aria-label="Ver la carta adjunta">
                                                                <IconoPdf />
                                                            </Button>
                                                        ) : null}
                                                    </span>
                                                ) : null}
                                                <span className="d-block">Formatos permitidos: {EXTENSIONES_CARTA.join(', ')} · Tamaño máximo: 4 MB.</span>
                                            </React.Fragment>
                                        )} />
                                </Row>
                            </Tab>
                        ) : null}

                        <Tab eventKey="siniestro" title={titulo(tab('siniestro'))}>
                            <Row>
                                <Texto formik={formik} name="hora" label="Hora siniestro" placeholder="Ej. 14:30" />
                                <Combo formik={formik} name="comunaSiniestro" label="Comuna siniestro" opciones={catalogos.comunas}
                                    onCambio={completaCiudad('ciudadSiniestro')} />
                                <TextoLectura label="Ciudad siniestro" valor={nombreCiudad(formik.values.comunaSiniestro)}
                                    ayuda="Se completa según la comuna." />
                                <Texto formik={formik} name="lugarSiniestro" label="Lugar del siniestro" md={6} />
                                <Combo formik={formik} name="causal" label="Causal" md={6} opciones={catalogos.causales} />
                                <Texto formik={formik} name="poliza" label="Póliza" />
                                <Texto formik={formik} name="itemPoliza" label="Ítem" inputMode="numeric" maxLen={5} />
                                <Texto formik={formik} name="vigenciaDesde" label="Vigencia desde" type="date" />
                                <Texto formik={formik} name="vigenciaHasta" label="Vigencia hasta" type="date" />
                                <Texto formik={formik} name="relato" label="Relato" md={12} rows={5} />
                            </Row>
                        </Tab>

                        <Tab eventKey="vehiculo" title={titulo(tab('vehiculo'))}>
                            <Row>
                                <Combo formik={formik} name="tipoVehiculo" label="Tipo de vehículo" opciones={catalogos.tiposVehiculo} />
                                <Combo formik={formik} name="marcaVehiculo" label="Marca" opciones={catalogos.marcas} onCambio={cargaModelos} />
                                <Combo formik={formik} name="codModelo" label="Modelo" opciones={modelos}
                                    deshabilitado={!formik.values.marcaVehiculo || cargandoModelos} ayuda={ayudaModelo}
                                    onCambio={(id) => formik.setFieldValue('modelo', nombreDe(modelos, id))} />
                                <Texto formik={formik} name="anioVehiculo" label="Año" placeholder="Ej. 2019" inputMode="numeric" maxLen={4} />
                                <Combo formik={formik} name="colorVehiculo" label="Color" opciones={catalogos.colores} />
                                <Combo formik={formik} name="usoVehiculo" label="Uso" opciones={catalogos.usos} />
                            </Row>
                        </Tab>

                        <Tab eventKey="asegurado" title={titulo(tab('asegurado'))}>
                            <Row>
                                <Rut formik={formik} name="aseRut" label="Rut" />
                                <Texto formik={formik} name="aseNombre" label="Nombre" md={6} />
                                <Texto formik={formik} name="aseFono" label="Teléfono" inputMode="tel" maxLen={20} />
                                <Texto formik={formik} name="aseDireccion" label="Dirección" md={6} />
                                <Combo formik={formik} name="aseComuna" label="Comuna" opciones={catalogos.comunas}
                                    onCambio={completaCiudad('aseCiudad')} />
                                <TextoLectura label="Ciudad" valor={nombreCiudad(formik.values.aseComuna)}
                                    ayuda="Se completa según la comuna." />
                            </Row>
                        </Tab>

                        {/* En cartas inter cía el tercero va en su propio bloque, a la vista; esta pestaña es solo de Recupero. */}
                        {defensa ? null : (
                            <Tab eventKey="tercero" title={titulo(tab('tercero'))}>
                                <Row>
                                    <Rut formik={formik} name="terRut" label="Rut tercero" />
                                    <Texto formik={formik} name="terNombre" label="Nombre tercero" />
                                    <Texto formik={formik} name="terPatente" label="Patente tercero" />
                                    <Combo formik={formik} name="terMarca" label="Marca tercero" opciones={catalogos.marcas} />
                                    <Combo formik={formik} name="idCiaImplicado" label="Compañía del tercero" md={6} opciones={catalogos.companias}
                                        ayuda="El abogado también puede completarla al derivar a Inter Cía." />
                                    <Texto formik={formik} name="siniestroIM" label="N° siniestro en la CIA" md={6} />
                                    <Texto formik={formik} name="fechaSiniestroIM" label="Fecha siniestro en la CIA" type="date" max={hoy} />
                                    <Texto formik={formik} name="terDescripcion" label="Descripción" md={9} />
                                </Row>
                            </Tab>
                        )}

                        <Tab eventKey="conductor" title={titulo(tab('conductor'))}>
                            <Row>
                                <Rut formik={formik} name="conRut" label="Rut" />
                                <Texto formik={formik} name="conNombre" label="Nombre" md={6} />
                                <Texto formik={formik} name="conFono" label="Teléfono" inputMode="tel" maxLen={20} />
                            </Row>
                        </Tab>

                        {defensa ? null : (
                            <Tab eventKey="montos" title={titulo(tab('montos'))}>
                                <Row>
                                    <Monto formik={formik} name="costo" label="Costo del siniestro" placeholder="Ej. 1.500.000" />
                                    <Monto formik={formik} name="provision" label="Provisión" placeholder="Ej. 1.200.000" />
                                    <div className="col-md-3 mb-3">
                                        <label className="form-label mb-1"><b>Moneda</b></label>
                                        <input className="form-control" value="CLP" readOnly />
                                    </div>
                                </Row>
                            </Tab>
                        )}

                        <Tab eventKey="policial" title={titulo(tab('policial'))}>
                            <Row>
                                <Texto formik={formik} name="comisaria" label="Comisaría" md={6} />
                                <Texto formik={formik} name="folioPolicial" label="Folio" inputMode="numeric" placeholder="Solo números" maxLen={10} />
                                <Texto formik={formik} name="fechaConstancia" label="Fecha constancia" type="date" max={hoy} />
                            </Row>
                        </Tab>

                    </Tabs>
                </Card.Body>
            </Card>

            <div className="d-grid gap-2 d-md-flex justify-content-md-end mb-4">
                {modoEdicion ? (
                    <Button className="me-md-2" variant="outline-secondary" type="button" onClick={onCancelarEdicion}>Cancelar edición</Button>
                ) : (
                    <Button className="me-md-2" variant="warning" type="button" onClick={() => { formik.resetForm(); setModelos([]); marcaSolicitada.current = ''; }}>Limpiar</Button>
                )}
                <Button variant="danger" type="submit" disabled={enviando}>
                    {enviando
                        ? <span><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Revisando...</span>
                        : 'Revisar'}
                </Button>
            </div>

        </form>
    );
};

export default Formulario;
